const Path = require( '@leverege/path' )

const SPLIT = /[/.]/

/**
 * Creates a function that will shallow copy the model and set the
 * key to an empty array.
 * @param {string} key the name of the field
 * @param {object} [options]
 * @param {function} [options.validate] ( model, key ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createClearArray( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model ) => {
      if ( options && options.validate ) {
        options.validate( model, key )
      }

      return path.copy( model, [] )
    }
  }

  return ( model ) => {
    if ( options && options.validate ) {
      options.validate( model, key )
    }

    const c = { ...model }
    c[key] = []
    return c
  }
}

/**
 * Creates a function that will shallow copy the model and set the
 * key to the given array.
 * @param {string} key the name of the field
 * @param {object} [options]
 * @param {boolean} [options.copy] if true, a new array will be made and the values
 * in array will be copied into it
 * @param {function} [options.validate] ( model, key ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createSetArray( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, array ) => {
      if ( !Array.isArray( array ) ) {
        throw new Error( 'SetArray must be given an array' )
      }
      if ( options && options.validate ) {
        options.validate( model, key, array )
      }

      return path.copy(
        model,
        options && options.copy === true ? [ ...array ] : array
      )
    }
  }

  return ( model, array ) => {
    if ( !Array.isArray( array ) ) {
      throw new Error( 'SetArray must be given an array' )
    }
    if ( options && options.validate ) {
      options.validate( model, key, array )
    }
    const c = { ...model }
    c[key] = options && options.copy === true ? [ ...array ] : array
    return c
  }
}

/**
 * Creates a shallow copy of the model, and sets the item at the
 * specified index. If index is out of bounds, an error is thrown
 * @param {string} key the name of the field
 * @param {object} options 
 * @param {string} [options.mode] if 'gteAdd', an index greater than the
 * current length of the array will append to the end. If 'sparse', the item
 * will be inserted in the Array at the index. Otherwise, an exception is thrown
 * @param {function} [options.validate] ( model, key, item, index ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createSetArrayItem( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, index, item ) => {
      const mode = options && options.mode
      const isArray = Array.isArray( path.get( model ) )
      const len = isArray ? path.get( model ).length : 0
      if ( index < 0 ) {
        throw new Error( `Replace Item Index out of bounds ${index}/${len}` )
      }
      if ( index >= len ) {
        if ( mode === 'gteAdd' ) {
          return createAddArrayItem( key )( model, item )
        } 
        if ( mode !== 'sparse' ) {
          throw new Error( `Replace Item Index out of bounds ${index}/${len}` )
        }
      }

      if ( path.get( model ) && path.get( model )[index] === item ) {
        return model
      }

      if ( options && options.validate ) {
        options.validate( model, key, item, index )
      }

      const newArr = isArray ? [ ...path.get( model ) ] : []
      newArr[index] = item

      return path.copy( model, newArr )
    }
  }

  return ( model, index, item ) => {
    
    const mode = options && options.mode
    const isArray = Array.isArray( model[key] )
    const len = isArray ? model[key].length : 0
    if ( index < 0 ) {
      throw new Error( `Replace Item Index out of bounds ${index}/${len}` )
    }
    if ( index >= len ) {
      if ( mode === 'gteAdd' ) {
        return createAddArrayItem( key )( model, item )
      } 
      if ( mode !== 'sparse' ) {
        throw new Error( `Replace Item Index out of bounds ${index}/${len}` )
      }
    }

    if ( model[key] && model[key][index] === item ) {
      return model
    }

    if ( options && options.validate ) {
      options.validate( model, key, item, index )
    }

    const c = { ...model }
    c[key] = isArray ? [ ...model[key] ] : []
    c[key][index] = item
    return c
  }
}

/**
 * Returns a function that returns the array of items
 * @param {string} key the name of the field
 * @param {object} [options]
 * @param {function} [options.copy] if true, a copy of the array is returned
 **/
function createGetArray( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model ) => {
      return path.get( model ) == null ?
        [] :
        options && options.copy ?
          [ ...path.get( model ) ] :
          path.get( model )
    }
  }

  return ( model ) => {
    if ( options && options.copy === true ) {
      return model[key] == null ? [] : [ ...model[key] ]
    }
    return model[key] == null ? [] : model[key]
  }
}

/**
 * Returns a function that returns the item at the index
 * @param {string} key the name of the field
 **/
function createGetArrayItem( key ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, index ) => {
      return path.get( model ) == null ? null : path.get( model )[index]
    }
  }

  return ( model, index ) => {
    return model[key] == null ? null : model[key][index]
  }
}

/**
 * Returns a function that returns the index of item in the array
 * @param {string} key the name of the field
**/
function createIndexInArray( key ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, item ) => {
      return path.get( model ) == null ? -1 : path.get( model ).indexOf( item )
    }
  }

  return ( model, item ) => {
    return model[key] == null ? -1 : model[key].indexOf( item )
  }
}

/**
 * Creates a shallow copy of the model, and adds the item
 * @param {string} key the name of the array field
 * @param {object} [options]
 * @param {function} [options.validate] ( model, key ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createAddArrayItem( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, item, insertAt = null ) => {
      if ( options && options.validate ) {
        options.validate( model, key, item, insertAt )
      }

      const c = path.copy(
        model,
        Array.isArray( path.get( model ) ) ?
          [ ...path.get( model ) ] :
          []
      )

      if ( typeof insertAt === 'number' ) {
        path.get( c ).splice( insertAt, 0, item )
      } else {
        path.get( c ).push( item )
      }

      return c
    }
  }

  return ( model, item, insertAt = null ) => {
    if ( options && options.validate ) {
      options.validate( model, key, item, insertAt )
    }
    const c = { ...model }
    c[key] = Array.isArray( model[key] ) ? [ ...model[key] ] : []
    if ( typeof insertAt === 'number' ) {
      c[key].splice( insertAt, 0, item )
    } else {
      c[key].push( item )
    }
    return c
  }
}

function createMoveArrayItem( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, from, to ) => {
      let arr = path.get( model )
      if ( arr == null ) {
        throw new Error( 'There are no items to move.' )
      }
      const len = arr.length
      if ( to < 0 || from < 0 || to >= len || from >= len ) {
        throw new Error( `Move Item Index out of bounds. Range : (0-${len - 1}). from = ${from} to = ${to}` )
      }
      if ( from === to ) {
        return model
      }

      arr = [ ...arr ] // copy array
      const elem = arr[from]
      if ( from < to ) {
        for ( let n = from; n < to; n++ ) {
          arr[n] = arr[n + 1]
        }
      } else {
        for ( let n = from; n > to; n-- ) {
          arr[n] = arr[n - 1]
        }
      }
      arr[to] = elem

      return path.copy( model, arr )
    }
  }

  return ( model, from, to ) => {
    let arr = model[key]
    if ( arr == null ) {
      throw new Error( 'There are no items to move.' )
    }
    const len = arr.length
    if ( to < 0 || from < 0 || to >= len || from >= len ) {
      throw new Error( `Move Item Index out of bounds. Range : (0-${len - 1}). from = ${from} to = ${to}` )
    }
    if ( from === to ) {
      return model
    }
    arr = [ ...arr ] // copy array
    const elem = arr[from]
    if ( from < to ) {
      for ( let n = from; n < to; n++ ) {
        arr[n] = arr[n + 1]
      }
    } else {
      for ( let n = from; n > to; n-- ) {
        arr[n] = arr[n - 1]
      }
    }
    arr[to] = elem

    const c = { ...model }
    c[key] = arr
    return c
  } 
}
/**
 * Creates a shallow copy of the model, and swaps the layers.
 * An error is thrown if the indices are out of bounds
 * @param {string} key the name of the array field
 * @param {object} [options]
 * @param {function} [options.validate] ( model, key, index1, index2 ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createSwapArrayItems( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, index1, index2 ) => {
      const len = Array.isArray( path.get( model ) ) ? path.get( model ).length : 0
      if ( index1 < 0 || index2 < 0 || index1 >= len || index2 >= len ) {
        throw new Error( `Swap Layers Index out of bounds. Swap ${
          index1} with ${index2}. Array length is ${len}` )
      } 

      if ( options && options.validate ) {
        options.validate( model, key, index1, index2 )
      }

      const c = path.copy( model, [ ...path.get( model ) ] )

      const t = path.get( c )[index1]
      path.get( c )[index1] = path.get( c )[index2]
      path.get( c )[index2] = t
      return c
    }
  }

  return ( model, index1, index2 ) => {
    
    const len = Array.isArray( model[key] ) ? model[key].length : 0
    if ( index1 < 0 || index2 < 0 || index1 >= len || index2 >= len ) {
      throw new Error( `Swap Layers Index out of bounds. Swap ${
        index1} with ${index2}. Array length is ${len}` )
    } 

    if ( options && options.validate ) {
      options.validate( model, key, index1, index2 )
    }

    const c = { ...model }
    c[key] = [ ...model[key] ]
    const t = c[key][index1]
    c[key][index1] = c[key][index2]
    c[key][index2] = t
    return c
  }
}

/**
 * Creates a shallow copy of the model, and removes the item.
 * @param {string} key the name of the array field
 * @param {object} [options]
 * @param {function} [options.validate] ( model, key, item, index ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createRemoveArrayItem( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, item ) => {
      if ( !Array.isArray( path.get( model ) ) ) {
        return model
      }
      const index = path.get( model ).indexOf( item )
    
      if ( index < 0 || index >= path.get( model ).length ) {
        return model
      }
  
      if ( options && options.validate ) {
        options.validate( model, key, item, index )
      }

      const c = path.copy( model, [ ...path.get( model ) ] )
      path.get( c ).splice( index, 1 )
      return c
    }
  }

  return ( model, item ) => {
    if ( !Array.isArray( model[key] ) ) {
      return model
    }
    const index = model[key].indexOf( item )
  
    if ( index < 0 || index >= model[key].length ) {
      return model
    }

    if ( options && options.validate ) {
      options.validate( model, key, item, index )
    }
    
    const c = { ...model }
    c[key] = [ ...model[key] ]  
    c[key].splice( index, 1 )
    return c
  }
}

/**
 * Creates a shallow copy of the model, and removes the item
 * at the given index. An error is thrown if the indices are out of 
 * bounds
 * @param {string} key the name of the array field
 * @param {object} [options]
 * @param {function} [options.validate] ( model, key, item, index ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createRemoveArrayItemAt( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, index ) => {
      if ( !Array.isArray( path.get( model ) ) || typeof index !== 'number' ) {
        return model
      }
      if ( index < 0 || index >= path.get( model ).length ) {
        return model
      }
      if ( options && options.validate ) {
        options.validate( model, key, path.get( model )[index], index )
      }

      const c = path.copy( model, [ ...path.get( model ) ] )
      path.get( c ).splice( index, 1 )
      return c
    }
  }

  return ( model, index ) => {
    if ( !Array.isArray( model[key] ) || typeof index !== 'number' ) {
      return model
    }
    if ( index < 0 || index >= model[key].length ) {
      return model
    }
    if ( options && options.validate ) {
      options.validate( model, key, model[key][index], index )
    }
    const c = { ...model }
    c[key] = [ ...model[key] ]  
    c[key].splice( index, 1 )
    return c
  }
}

function createAllArray( key, name, namePlural ) {
  if ( name == null ) {
    if ( key[key.length - 1] === 's' ) {
      // eslint-disable-next-line no-param-reassign
      name = `${key[0].toUpperCase()}${key.substring( 1, key.length - 1 )}`
    } else {
      // eslint-disable-next-line no-param-reassign
      name = `${key[0].toUpperCase()}${key.substring( 1 )}`
    }
  } else {
    // eslint-disable-next-line no-param-reassign
    name = `${name[0].toUpperCase() + name.substring( 1 )}`
  }

  if ( namePlural == null ) {
    if ( key[key.length - 1] === 's' ) {
      // eslint-disable-next-line no-param-reassign
      namePlural = `${key[0].toUpperCase() + key.substring( 1 )}`
    } else {
      // eslint-disable-next-line no-param-reassign
      namePlural = `${key[0].toUpperCase() + key.substring( 1 )}s`
    }
  } else {
    // eslint-disable-next-line no-param-reassign
    namePlural = `${namePlural[0].toUpperCase() + namePlural.substring( 1 )}`
  }
  
  const methods = {
    [`add${name}`] : createAddArrayItem( key ),
    [`remove${name}`] : createRemoveArrayItem( key ),
    [`remove${name}At`] : createRemoveArrayItemAt( key ),
    [`set${name}`] : createSetArrayItem( key ),
    [`swap${namePlural}`] : createSwapArrayItems( key ),
    [`move${name}`] : createMoveArrayItem( key ),
    [`clear${namePlural}`] : createClearArray( key ),
    [`set${namePlural}`] : createSetArray( key ),
    [`get${name}`] : createGetArrayItem( key ),
    [`get${namePlural}`] : createGetArray( key ),
    [`indexOf${name}`] : createIndexInArray( key ),
  }
  const normalized = {
    add : methods[`add${name}`],
    remove : methods[`remove${name}`],
    removeAt : methods[`remove${name}At`],
    set : methods[`set${name}`],
    swap : methods[`swap${namePlural}`],
    move : methods[`move${name}`],
    clear : methods[`clear${namePlural}`],
    setAll : methods[`set${namePlural}`],
    get : methods[`get${name}`],
    getAll : methods[`get${namePlural}`],
    indexOf : methods[`indexOf${name}`],
  }
  methods[`${namePlural[0].toLowerCase() + namePlural.substring( 1 )}`] = ( ) => normalized
  return methods
}

exports.createSetArray = createSetArray
exports.createClearArray = createClearArray
exports.createSetArrayItem = createSetArrayItem
exports.createAddArrayItem = createAddArrayItem
exports.createMoveArrayItem = createMoveArrayItem
exports.createSwapArrayItems = createSwapArrayItems
exports.createRemoveArrayItem = createRemoveArrayItem
exports.createRemoveArrayItemAt = createRemoveArrayItemAt
exports.createGetArray = createGetArray
exports.createIndexInArray = createIndexInArray
exports.createAllArray = createAllArray
